import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bffApiClient } from '@api/bff/client';

export type RackCellAssignmentDto = {
  cell: string;
  equipmentRef: string;
  scriptGlobalId: string;
  overridden: boolean;
};

export type RackAssignmentViewModel = {
  jobNumber: string;
  cells: RackCellAssignmentDto[];
  updatedAt: string | null;
};

export type UpsertRackAssignmentRequest = {
  cells: RackCellAssignmentDto[];
};

const RACK_ASSIGNMENTS_QUERY_KEY = 'rackAssignments';

export const rackAssignmentKeys = {
  byJobNumber: (jobNumber: string) =>
    [RACK_ASSIGNMENTS_QUERY_KEY, jobNumber] as const
};

export const useRackAssignmentsQuery = (jobNumber: string, enabled = true) => {
  return useQuery<RackAssignmentViewModel, Error>({
    queryKey: rackAssignmentKeys.byJobNumber(jobNumber),
    queryFn: async () => {
      const response = await bffApiClient.get<RackAssignmentViewModel>(
        `/api/work-order-rack-assignments/${encodeURIComponent(jobNumber)}`
      );
      return response.data;
    },
    enabled: enabled && !!jobNumber,
    staleTime: 30_000
  });
};

export const useUpsertRackAssignments = (jobNumber: string) => {
  const queryClient = useQueryClient();

  return useMutation<
    RackAssignmentViewModel,
    Error,
    UpsertRackAssignmentRequest
  >({
    mutationFn: async payload => {
      const response = await bffApiClient.put<RackAssignmentViewModel>(
        `/api/work-order-rack-assignments/${encodeURIComponent(jobNumber)}`,
        payload
      );
      return response.data;
    },
    onSuccess: data => {
      queryClient.setQueryData(
        rackAssignmentKeys.byJobNumber(jobNumber),
        data
      );
    }
  });
};
